import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Columns3,
  Grid2X2,
  List,
  Plus,
  Printer,
  RotateCcw,
  Search,
  SlidersHorizontal,
  User,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { EmptyState } from '@/components/common/EmptyState.js';
import { WarrantyBadge } from '@/components/tickets/WarrantyBadge.js';
import { TicketCardSkeleton } from '@/components/common/LoadingSkeleton.js';
import { Pagination } from '@/components/common/Pagination.js';
import {
  assignTicket,
  bulkDeleteTickets,
  bulkUpdateTickets,
  getTicketCounts,
  listTickets,
  updateTicketStatus,
  type TicketListItem,
} from '@/lib/api/tickets.js';
import { listUsers } from '@/lib/api/users.js';
import { StatusBadge, formatTicketStatus, useTicketStatusCatalog } from '@/components/tickets/StatusBadge.js';
import type { TicketStatusRecord } from '@/lib/api/ticket-statuses.js';
import { TicketKanbanBoard } from '@/components/tickets/TicketKanbanBoard.js';
import { DeleteTicketModal } from '@/components/tickets/DeleteTicketModal.js';
import { formatCurrency, formatDateTime, prettyLabel } from '@/lib/format.js';
import { depositRemaining } from '@/lib/deposit.js';
import { formatDueCountdown, formatOverdueAgo, isTicketOverdue } from '@/lib/ticket-due.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastInfo, toastSuccess } from '@/lib/toast.js';
import { clearRunningTimer, readRunningTimer, writeRunningTimer } from '@/lib/running-timer-storage.js';
import { useAuthStore } from '@/stores/auth.js';
import { useUserPreferences } from '@/contexts/UserPreferencesContext.js';
import { ticketDetailPath } from '@/lib/ticket-detail-path.js';
import { CustomerStatusBadge } from '@/components/common/CustomerStatusBadge.js';
import { CloneTicketModal } from '@/components/tickets/CloneTicketModal.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { isManagerRole } from '@/lib/permissions.js';
import { wsManager } from '@/lib/websocket-manager.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';

type TicketStatus = string;
type TicketPriority = 'normal' | 'rush' | 'urgent';
type SortKey = 'priority' | 'newest' | 'oldest' | 'due_date' | 'customer';
type TableSortKey = 'ticket' | 'customer' | 'due_date' | 'balance' | 'status';
type ImportedFromFilter = '' | 'native' | 'repairdesk';

interface TicketFilters {
  search: string;
  statuses: TicketStatus[];
  priority: TicketPriority[];
  assignedTo: string | null;
  overdueOnly: boolean;
  unassignedOnly: boolean;
  warrantyOnly: boolean;
  importedFrom: ImportedFromFilter;
  createdFrom: string | null;
  createdTo: string | null;
  dueFrom: string | null;
  dueTo: string | null;
  sort: SortKey;
  page: number;
  pageSize: number;
}

const ARCHIVE_STAGES = new Set(['closed', 'cancelled']);
const FALLBACK_PRIMARY = [
  'intake',
  'in_queue',
  'diagnosing',
  'in_repair',
  'waiting_parts',
  'waiting_auth',
  'on_hold',
  'ready',
  'pending_pay',
];
const FALLBACK_ARCHIVE = ['closed', 'warranty', 'cancelled'];
const BULK_STATUS_OPTIONS: TicketStatus[] = [
  'in_queue',
  'diagnosing',
  'in_repair',
  'waiting_parts',
  'on_hold',
  'ready',
];
const ASSIGNABLE_ROLES = [
  'technician',
  'store_manager',
  'tenant_admin',
  'front_desk',
  'platform_admin',
];
const DEFAULT_FILTERS: TicketFilters = {
  search: '',
  statuses: [],
  priority: [],
  assignedTo: null,
  overdueOnly: false,
  unassignedOnly: false,
  warrantyOnly: false,
  importedFrom: '',
  createdFrom: null,
  createdTo: null,
  dueFrom: null,
  dueTo: null,
  sort: 'priority',
  page: 1,
  pageSize: 25,
};
const FILTER_STORAGE_KEY = 'fixflow.ticketQueue.filters';
const VIEW_STORAGE_KEY_NEW = 'fixflow_ticket_view';
const VIEW_STORAGE_KEY_OLD = 'fixflow.ticketQueue.view';
const MY_TICKETS_STORAGE_KEY = 'fixflow_queue_my_tickets';

function loadSavedFilters(): TicketFilters {
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    const base = raw ? { ...DEFAULT_FILTERS, ...JSON.parse(raw) } : { ...DEFAULT_FILTERS };
    if (loadUnassignedPref()) {
      base.unassignedOnly = true;
    }
    return base;
  } catch {
    return loadUnassignedPref()
      ? { ...DEFAULT_FILTERS, unassignedOnly: true }
      : DEFAULT_FILTERS;
  }
}

function loadView(): 'list' | 'card' | 'kanban' {
  const newVal = window.localStorage.getItem(VIEW_STORAGE_KEY_NEW);
  if (newVal === 'list') return 'list';
  if (newVal === 'cards') return 'card';
  if (newVal === 'kanban') return 'kanban';
  const oldVal = window.localStorage.getItem(VIEW_STORAGE_KEY_OLD);
  if (oldVal === 'list') return 'list';
  if (oldVal === 'card') return 'card';
  return 'card';
}

function loadMyTickets(): boolean {
  return window.localStorage.getItem(MY_TICKETS_STORAGE_KEY) === 'true';
}

function loadUnassignedPref(): boolean {
  try {
    const raw = localStorage.getItem('fixflow_queue_filter_pref');
    return raw === 'unassigned';
  } catch {
    return false;
  }
}

// Ready/closed/cancelled/voided tickets are never overdue — they're done.
function isClosed(ticket: TicketListItem) {
  return ['closed', 'cancelled', 'voided', 'ready'].includes(ticket.status);
}

function isOverdue(ticket: TicketListItem) {
  if (isClosed(ticket)) return false;
  return isTicketOverdue(ticket.promisedDate, ticket.status);
}

function dueLabel(value?: string | null) {
  if (!value) return 'No due time';
  const due = new Date(value);
  const now = new Date();
  const sameDay = due.toDateString() === now.toDateString();
  return `Due ${new Intl.DateTimeFormat('en-US', {
    weekday: sameDay ? undefined : 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(due)}`;
}

function activeFilterCount(filters: TicketFilters) {
  let count = 0;
  if (filters.search.trim()) count += 1;
  if (filters.statuses.length) count += 1;
  if (filters.priority.length) count += 1;
  if (filters.assignedTo) count += 1;
  if (filters.overdueOnly) count += 1;
  if (filters.warrantyOnly) count += 1;
  if (filters.unassignedOnly) count += 1;
  if (filters.importedFrom) count += 1;
  if (filters.createdFrom || filters.createdTo) count += 1;
  if (filters.dueFrom || filters.dueTo) count += 1;
  if (filters.sort !== DEFAULT_FILTERS.sort) count += 1;
  return count;
}

// Overdue always wins the left-border color; urgent beats rush.
function cardBorderColor(ticket: TicketListItem, overdue: boolean) {
  if (overdue) return '#EF4444';
  if (ticket.isWarranty) return '#7C3AED';
  if (ticket.priority === 'urgent') return '#F97316';
  if (ticket.priority === 'rush') return '#F59E0B';
  return '#E2E8F0';
}

function priorityBarClass(ticket: TicketListItem, overdue: boolean) {
  if (overdue) return 'bg-red-500';
  if (ticket.isWarranty) return 'bg-violet-500';
  if (ticket.priority === 'urgent') return 'bg-orange-500';
  if (ticket.priority === 'rush') return 'bg-amber-500';
  return 'bg-slate-100';
}

function priorityBadgeClasses(priority: string) {
  if (priority === 'urgent') return 'bg-orange-100 text-orange-700';
  if (priority === 'rush') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-600';
}

function priorityBadgeLabel(priority: string) {
  if (priority === 'urgent') return '🔴 URGENT';
  if (priority === 'rush') return '🟠 RUSH';
  return prettyLabel(priority);
}

const DUE_TONE_CLASSES: Record<string, string> = {
  overdue: 'font-bold text-red-600',
  soon: 'font-semibold text-amber-600',
  today: 'font-semibold text-green-700',
  later: 'text-slate-500',
  none: 'text-slate-500',
};

function initials(name?: string | null) {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function firstDevice(ticket: TicketListItem) {
  return ticket.deviceSummary?.split(' + ')[0] ?? 'No device';
}

function RepairDeskBadge({ importedFrom }: { importedFrom?: string | null }) {
  if (importedFrom !== 'repairdesk') return null;
  return (
    <span
      className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600"
      title="Imported from RepairDesk"
    >
      RD
    </span>
  );
}

export function TicketQueuePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlCustomerId = searchParams.get('customerId') || undefined;
  const urlModelId = searchParams.get('modelId') || undefined;
  const urlCustomModel = searchParams.get('customModel') || undefined;
  const urlImportedFrom = searchParams.get('importedFrom');
  const queryClient = useQueryClient();
  const storeId = useAuthStore((s) => s.user?.storeId);
  const userRole = useAuthStore((s) => s.user?.role);
  const { preferences } = useUserPreferences();
  const openTicket = (ticketId: string) =>
    ticketDetailPath(ticketId, { role: userRole, pref: preferences?.ticketDetailView });
  const canClone = isManagerRole(userRole ?? '');
  const canDelete =
    userRole === 'tenant_admin' || userRole === 'platform_admin';
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();
  const [filters, setFilters] = useState<TicketFilters>(() => {
    const base = loadSavedFilters();
    if (urlImportedFrom === 'repairdesk' || urlImportedFrom === 'native') {
      return {
        ...base,
        importedFrom: urlImportedFrom,
        ...(urlImportedFrom === 'repairdesk' && base.statuses.length === 0
          ? { statuses: ['closed' as TicketStatus] }
          : {}),
      };
    }
    return base;
  });
  const [searchInput, setSearchInput] = useState(filters.search);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [view, setView] = useState<'card' | 'list' | 'kanban'>(() => loadView());
  const [myTickets, setMyTickets] = useState<boolean>(() => loadMyTickets());
  const [cloneTarget, setCloneTarget] = useState<{ id: string; ticketNumber: string; customerId?: string | null } | null>(null);
  const storeQueryForLabels = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const vipLabel = storeQueryForLabels.data?.vipLabel;
  const flaggedLabel = storeQueryForLabels.data?.flaggedLabel;
  const statusesCatalogQuery = useTicketStatusCatalog(true);
  const statusCatalog = statusesCatalogQuery.data ?? [];
  const primaryStatusPills = useMemo(() => {
    const active = statusCatalog.filter((s) => s.active && !ARCHIVE_STAGES.has(String(s.stage)));
    if (active.length === 0) return FALLBACK_PRIMARY;
    return active.sort((a, b) => a.sortOrder - b.sortOrder).map((s) => s.slug);
  }, [statusCatalog]);
  const archiveStatusPills = useMemo(() => {
    const archived = statusCatalog.filter(
      (s) => s.active && (ARCHIVE_STAGES.has(String(s.stage)) || s.slug === 'warranty'),
    );
    if (archived.length === 0) return FALLBACK_ARCHIVE;
    return archived.sort((a, b) => a.sortOrder - b.sortOrder).map((s) => s.slug);
  }, [statusCatalog]);
  const bulkStatusOptions = useMemo(() => {
    const opts = statusCatalog.filter(
      (s) => s.active && !ARCHIVE_STAGES.has(String(s.stage)) && s.slug !== 'quote',
    );
    if (opts.length === 0) return BULK_STATUS_OPTIONS;
    return opts.sort((a, b) => a.sortOrder - b.sortOrder).map((s) => s.slug);
  }, [statusCatalog]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TicketListItem | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteReason, setBulkDeleteReason] = useState('');
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState('');
  const [, setTick] = useState(0);

  useEffect(() => {
    if (urlImportedFrom !== 'repairdesk' && urlImportedFrom !== 'native') return;
    setFilters((current) => {
      if (current.importedFrom === urlImportedFrom) return current;
      return {
        ...current,
        importedFrom: urlImportedFrom,
        page: 1,
        ...(urlImportedFrom === 'repairdesk' && current.statuses.length === 0
          ? { statuses: ['closed'] }
          : {}),
      };
    });
  }, [urlImportedFrom]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setFilters((current) => ({ ...current, search: searchInput, page: 1 }));
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    window.localStorage.setItem(VIEW_STORAGE_KEY_NEW, view === 'card' ? 'cards' : view);
  }, [view]);

  useEffect(() => {
    window.localStorage.setItem(MY_TICKETS_STORAGE_KEY, String(myTickets));
  }, [myTickets]);

  // Force a re-render every minute so overdue/countdown labels stay fresh.
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [filters, myTickets]);

  useEffect(() => {
    if (!storeId) return;
    const queueChannel = `store:${storeId}:queue`;
    wsManager.connect(queueChannel, `/ws/queue/${storeId}`);
    const unsub = wsManager.on(queueChannel, (data) => {
      if (data.type === 'queue.counts_updated' && data.payload) {
        queryClient.setQueryData(['ticket-counts'], data.payload);
      }
      if (
        data.type === 'ticket.status_changed' ||
        data.type === 'queue.update' ||
        data.type === 'ticket.assigned' ||
        data.type === 'ticket.deleted'
      ) {
        queryClient.invalidateQueries({ queryKey: ['tickets'] });
        if (data.type !== 'queue.counts_updated') {
          queryClient.invalidateQueries({ queryKey: ['ticket-counts'] });
        }
      }
    });
    return () => unsub();
  }, [storeId, queryClient]);

  const now = Date.now();

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: listUsers });
  const assignableUsers = useMemo(
    () => (usersQuery.data ?? []).filter((u) => u.active !== false && ASSIGNABLE_ROLES.includes(u.role)),
    [usersQuery.data],
  );
  const countsQuery = useQuery({
    queryKey: ['ticket-counts', storeId],
    queryFn: () => getTicketCounts(),
    enabled: Boolean(storeId),
  });
  const ticketsQuery = useQuery({
    queryKey: [
      'tickets',
      storeId,
      filters,
      myTickets,
      view === 'kanban',
      urlCustomerId,
      urlModelId,
      urlCustomModel,
    ],
    enabled: Boolean(storeId),
    queryFn: () => {
      const isKanban = view === 'kanban';
      const params: Parameters<typeof listTickets>[0] = {
        page: isKanban ? 1 : filters.page,
        pageSize: isKanban ? 500 : filters.pageSize,
        sort: filters.sort,
        excludeSource: 'pos_sale',
      };
      if (storeId) params.storeId = storeId;
      if (filters.search) params.search = filters.search;
      if (filters.statuses.length) params.status = filters.statuses.join(',');
      if (filters.priority.length) params.priority = filters.priority.join(',');
      if (filters.unassignedOnly) params.assignedTo = 'unassigned';
      else if (filters.assignedTo) params.assignedTo = filters.assignedTo;
      if (filters.overdueOnly) params.overdueOnly = true;
      if (filters.warrantyOnly) params.warrantyOnly = true;
      if (myTickets) params.assignedToMe = true;
      if (filters.createdFrom) params.createdFrom = new Date(filters.createdFrom).toISOString();
      if (filters.createdTo) params.createdTo = new Date(filters.createdTo).toISOString();
      if (filters.dueFrom) params.dueFrom = new Date(filters.dueFrom).toISOString();
      if (filters.dueTo) params.dueTo = new Date(filters.dueTo).toISOString();
      if (filters.importedFrom) params.importedFrom = filters.importedFrom;
      if (urlCustomerId) params.customerId = urlCustomerId;
      if (urlModelId) params.modelId = urlModelId;
      if (urlCustomModel) params.customModelName = urlCustomModel;
      return listTickets(params);
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (body: { ticketIds: string[]; status?: string; assignedTo?: string | null }) =>
      bulkUpdateTickets(body),
    onSuccess: (result, variables) => {
      if (variables.status && variables.status !== 'in_repair') {
        const rt = readRunningTimer();
        if (rt && variables.ticketIds.includes(rt.ticketId)) clearRunningTimer();
      }
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-counts'] });
      toastSuccess(`Updated ${result.updated} ticket${result.updated === 1 ? '' : 's'}`);
      if (result.errors.length) {
        toastError(`${result.errors.length} ticket(s) could not be updated`);
      }
      setSelectedIds(new Set());
      setBulkStatus('');
      setBulkAssignTo('');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (body: {
      ticketIds: string[];
      reason: string;
      confirmText: 'DELETE';
      managerOverrideAuthorizedBy: string;
    }) => bulkDeleteTickets(body),
    onSuccess: (data) => {
      toastSuccess(`Deleted ${data.deletedCount} ticket(s)`);
      if (data.errors.length) {
        toastError(`${data.errors.length} ticket(s) could not be deleted`);
      }
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      setBulkDeleteReason('');
      setBulkDeleteConfirm('');
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['ticket-counts'] });
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  async function handleBulkDelete() {
    if (bulkDeleteConfirm !== 'DELETE' || bulkDeleteReason.trim().length < 3) return;
    const result = await requireOverride({
      action: 'delete_ticket',
      title: 'Manager Approval to Delete Tickets',
      description: `Permanently deleting ${selectedIds.size} ticket(s)`,
      requiredRole: 'tenant_admin',
    });
    if (!result.approved) return;
    bulkDeleteMutation.mutate({
      ticketIds: Array.from(selectedIds),
      reason: bulkDeleteReason.trim(),
      confirmText: 'DELETE',
      managerOverrideAuthorizedBy: result.authorizedBy.id,
    });
  }

  const tickets = ticketsQuery.data?.data ?? [];
  const ticketsMeta = ticketsQuery.data?.meta;
  const counts = countsQuery.data ?? {};
  const filterCount = activeFilterCount(filters);
  const allSelected = tickets.length > 0 && tickets.every((t) => selectedIds.has(t.id));

  function updateFilters(patch: Partial<TicketFilters>) {
    setFilters((current) => ({ ...current, ...patch, page: patch.page ?? 1 }));
  }

  function toggleStatus(status: TicketStatus, event?: MouseEvent<HTMLButtonElement>) {
    if (status === 'quote') {
      updateFilters({ statuses: [] });
      return;
    }
    const multi = Boolean(event?.ctrlKey || event?.metaKey);
    updateFilters({
      statuses: multi
        ? filters.statuses.includes(status)
          ? filters.statuses.filter((s) => s !== status)
          : [...filters.statuses, status]
        : [status],
    });
  }

  function removeTag(key: keyof TicketFilters, value?: string) {
    if (key === 'priority' && value)
      updateFilters({ priority: filters.priority.filter((p) => p !== value) });
    else if (key === 'statuses' && value)
      updateFilters({ statuses: filters.statuses.filter((s) => s !== value) });
    else updateFilters({ [key]: DEFAULT_FILTERS[key] } as Partial<TicketFilters>);
  }

  function toggleSelect(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds((current) => {
      if (tickets.length > 0 && tickets.every((t) => current.has(t.id))) return new Set();
      return new Set(tickets.map((t) => t.id));
    });
  }

  return (
    <div className="space-y-5">
      <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              placeholder="Search tickets, customers, devices..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="flex rounded-xl bg-slate-100 p-1">
              <button
                type="button"
                className={`rounded-lg p-2 ${view === 'list' ? 'bg-white shadow-sm' : ''}`}
                onClick={() => setView('list')}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                type="button"
                className={`rounded-lg p-2 ${view === 'card' ? 'bg-white shadow-sm' : ''}`}
                onClick={() => setView('card')}
                aria-label="Card view"
              >
                <Grid2X2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                className={`rounded-lg p-2 ${view === 'kanban' ? 'bg-white shadow-sm' : ''}`}
                onClick={() => setView('kanban')}
                aria-label="Kanban view"
              >
                <Columns3 className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setMyTickets((current) => !current)}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold ${
                myTickets
                  ? 'bg-primary text-white'
                  : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <User className="h-4 w-4" />
              My Tickets
            </button>
            <button
              type="button"
              onClick={() => navigate('/tickets/new')}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#d56b32]"
            >
              <Plus className="h-4 w-4" />
              New Ticket
            </button>
            <button
              type="button"
              onClick={() => navigate('/tickets/quick')}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Plus className="h-4 w-4" />
              Quick Ticket
            </button>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={(event) => toggleStatus('quote', event)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold ${filters.statuses.length === 0 ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'}`}
          >
            All Active ({counts.total_active ?? 0})
          </button>
          <button
            type="button"
            onClick={() => updateFilters({ overdueOnly: !filters.overdueOnly })}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold ${
              filters.overdueOnly ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 ring-1 ring-red-200'
            }`}
          >
            🔴 Overdue
            <span
              className={`rounded-full px-1.5 py-0.5 text-xs font-bold ${
                filters.overdueOnly ? 'bg-white/20' : 'bg-red-600 text-white'
              }`}
            >
              {counts.overdue ?? 0}
            </span>
          </button>
          {primaryStatusPills.map((status) => (
            <button
              key={status}
              type="button"
              onClick={(event) => toggleStatus(status, event)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold ${filters.statuses.includes(status) ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'}`}
            >
              {formatTicketStatus(status, statusCatalog)} ({counts[status] ?? 0})
            </button>
          ))}
          <div className="mx-1 h-8 w-px shrink-0 bg-slate-200" />
          {archiveStatusPills.map((status) => (
            <button
              key={status}
              type="button"
              onClick={(event) => toggleStatus(status, event)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold ${filters.statuses.includes(status) ? 'bg-slate-700 text-white' : 'bg-slate-50 text-slate-500 ring-1 ring-slate-200'}`}
            >
              {formatTicketStatus(status, statusCatalog)} ({counts[status] ?? 0})
            </button>
          ))}
        </div>

        <div>
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters ({filterCount} active) {filtersOpen ? '▲' : '▼'}
          </button>
          {filtersOpen ? (
            <div className="mt-3 grid gap-4 rounded-2xl bg-slate-50 p-4 lg:grid-cols-4">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Assigned To</span>
                <select
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={filters.assignedTo ?? ''}
                  onChange={(e) =>
                    updateFilters({ assignedTo: e.target.value || null, unassignedOnly: false })
                  }
                >
                  <option value="">Anyone</option>
                  {usersQuery.data?.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </label>
              <div>
                <span className="text-sm font-medium text-slate-700">Priority</span>
                <div className="mt-1 flex gap-2">
                  {(['normal', 'rush', 'urgent'] as const).map((priority) => (
                    <button
                      key={priority}
                      type="button"
                      onClick={() =>
                        updateFilters({
                          priority: filters.priority.includes(priority)
                            ? filters.priority.filter((p) => p !== priority)
                            : [...filters.priority, priority],
                        })
                      }
                      className={`rounded-xl px-3 py-2 text-sm font-semibold capitalize ${filters.priority.includes(priority) ? 'bg-primary text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'}`}
                    >
                      {priority[0]?.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-violet-800">
                <input
                  type="checkbox"
                  checked={filters.warrantyOnly}
                  onChange={(e) => updateFilters({ warrantyOnly: e.target.checked })}
                  className="h-4 w-4 rounded border-violet-300 text-violet-700 focus:ring-violet-500"
                />
                Warranty tickets only
              </label>
              <div className="grid gap-2">
                <span className="text-sm font-medium text-slate-700">Created Date</span>
                <input
                  type="date"
                  className="rounded-xl border border-slate-300 px-3 py-2"
                  value={filters.createdFrom ?? ''}
                  onChange={(e) => updateFilters({ createdFrom: e.target.value || null })}
                />
                <input
                  type="date"
                  className="rounded-xl border border-slate-300 px-3 py-2"
                  value={filters.createdTo ?? ''}
                  onChange={(e) => updateFilters({ createdTo: e.target.value || null })}
                />
              </div>
              <div className="grid gap-2">
                <span className="text-sm font-medium text-slate-700">Due Date</span>
                <input
                  type="date"
                  className="rounded-xl border border-slate-300 px-3 py-2"
                  value={filters.dueFrom ?? ''}
                  onChange={(e) => updateFilters({ dueFrom: e.target.value || null })}
                />
                <input
                  type="date"
                  className="rounded-xl border border-slate-300 px-3 py-2"
                  value={filters.dueTo ?? ''}
                  onChange={(e) => updateFilters({ dueTo: e.target.value || null })}
                />
              </div>
              <div className="flex flex-wrap gap-2 lg:col-span-4">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  Source / Import
                  <select
                    className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold"
                    value={filters.importedFrom}
                    onChange={(e) =>
                      updateFilters({
                        importedFrom: e.target.value as ImportedFromFilter,
                        ...(e.target.value === 'repairdesk' && filters.statuses.length === 0
                          ? { statuses: ['closed'] }
                          : {}),
                      })
                    }
                  >
                    <option value="">All</option>
                    <option value="native">Repairiz</option>
                    <option value="repairdesk">Imported from RepairDesk</option>
                  </select>
                </label>
                <button
                  type="button"
                  className={`rounded-xl px-3 py-2 text-sm font-semibold ${filters.unassignedOnly ? 'bg-primary text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'}`}
                  onClick={() =>
                    updateFilters({ unassignedOnly: !filters.unassignedOnly, assignedTo: null })
                  }
                >
                  👤 Unassigned Only
                </button>
                <select
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold"
                  value={filters.sort}
                  onChange={(e) => updateFilters({ sort: e.target.value as SortKey })}
                >
                  <option value="priority">Priority</option>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="due_date">Due Date</option>
                  <option value="customer">Customer</option>
                </select>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
                  onClick={() => {
                    setFilters(DEFAULT_FILTERS);
                    setSearchInput('');
                  }}
                >
                  <RotateCcw className="h-4 w-4" /> Reset All
                </button>
                <button
                  type="button"
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
                  onClick={() =>
                    window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters))
                  }
                >
                  💾 Save Filter
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {filterCount > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            {filters.search.trim() ? (
              <FilterTag
                label={`Search: ${filters.search}`}
                onRemove={() => {
                  updateFilters({ search: '' });
                  setSearchInput('');
                }}
              />
            ) : null}
            {filters.statuses.map((status) => (
              <FilterTag
                key={status}
                label={`Status: ${prettyLabel(status)}`}
                onRemove={() => removeTag('statuses', status)}
              />
            ))}
            {filters.priority.map((priority) => (
              <FilterTag
                key={priority}
                label={`Priority: ${prettyLabel(priority)}`}
                onRemove={() => removeTag('priority', priority)}
              />
            ))}
            {filters.assignedTo ? (
              <FilterTag
                label={`Assigned: ${usersQuery.data?.find((u) => u.id === filters.assignedTo)?.firstName ?? 'User'}`}
                onRemove={() => removeTag('assignedTo')}
              />
            ) : null}
            {filters.unassignedOnly ? (
              <FilterTag label="Unassigned" onRemove={() => removeTag('unassignedOnly')} />
            ) : null}
            {filters.overdueOnly ? (
              <FilterTag label="Overdue" onRemove={() => removeTag('overdueOnly')} />
            ) : null}
            {filters.importedFrom ? (
              <FilterTag
                label={
                  filters.importedFrom === 'repairdesk'
                    ? 'Imported from RepairDesk'
                    : 'Repairiz (native)'
                }
                onRemove={() => removeTag('importedFrom')}
              />
            ) : null}
            {filters.createdFrom || filters.createdTo ? (
              <FilterTag
                label="Created date"
                onRemove={() => updateFilters({ createdFrom: null, createdTo: null })}
              />
            ) : null}
            {filters.dueFrom || filters.dueTo ? (
              <FilterTag
                label="Due date"
                onRemove={() => updateFilters({ dueFrom: null, dueTo: null })}
              />
            ) : null}
            <button
              className="ml-auto text-sm font-semibold text-primary"
              onClick={() => {
                setFilters(DEFAULT_FILTERS);
                setSearchInput('');
              }}
            >
              Clear All Filters
            </button>
          </div>
        ) : null}
      </section>

      {view === 'kanban' ? (
        <TicketKanbanBoard
          tickets={tickets}
          users={assignableUsers}
          onStatusChange={async (ticketId, status) => {
            const result = await updateTicketStatus(ticketId, { status });
            if (result.startedTimer) {
              const ticket = tickets.find((t) => t.id === ticketId);
              writeRunningTimer({
                ticketId,
                ticketNumber: ticket?.ticketNumber ?? result.ticket.ticketNumber,
                entryId: result.startedTimer.id,
                ...(result.startedTimer.ticketRepairId
                  ? { repairId: result.startedTimer.ticketRepairId }
                  : {}),
                startedAt: result.startedTimer.startedAt,
                repairName: 'Repair',
              });
            } else if (status !== 'in_repair') {
              const rt = readRunningTimer();
              if (rt?.ticketId === ticketId) clearRunningTimer();
            }
            queryClient.invalidateQueries({ queryKey: ['tickets'] });
            queryClient.invalidateQueries({ queryKey: ['ticket-counts'] });
          }}
          onAssign={async (ticketId, technicianId) => {
            await assignTicket(ticketId, { technicianId });
            queryClient.invalidateQueries({ queryKey: ['tickets'] });
          }}
          onOpen={(ticketId) => navigate(openTicket(ticketId))}
        />
      ) : view === 'card' ? (
        <div className="grid gap-3 xl:grid-cols-2">
          {tickets.map((ticket) => {
            const overdue = isOverdue(ticket);
            const countdown = formatDueCountdown(ticket.promisedDate, ticket.status, now);
            const selected = selectedIds.has(ticket.id);
            return (
              <Link
                key={ticket.id}
                to={openTicket(ticket.id)}
                className={`overflow-hidden rounded-2xl border shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${selected ? 'ring-2 ring-primary/40' : ''} ${ticket.isWarranty ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-white'}`}
                style={{ borderLeftWidth: 4, borderLeftColor: cardBorderColor(ticket, overdue) }}
              >
                <div className={`h-1 ${priorityBarClass(ticket, overdue)}`} />
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={selected}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleSelect(ticket.id);
                        }}
                        onChange={() => {}}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-primary focus:ring-primary"
                        aria-label={`Select ticket ${ticket.ticketNumber}`}
                      />
                      <span className="inline-flex items-center gap-1.5 text-lg font-black text-slate-950">
                        {ticket.ticketNumber}
                        <RepairDeskBadge importedFrom={ticket.importedFrom} />
                        <WarrantyBadge isWarranty={ticket.isWarranty} />
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <StatusBadge status={ticket.status} catalog={statusCatalog} />
                      {overdue && ticket.promisedDate ? (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-red-600 px-2.5 py-1 text-xs font-bold text-white">
                          🔴 OVERDUE · {formatOverdueAgo(ticket.promisedDate, now)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-4 font-semibold text-slate-950">
                    {ticket.deviceSummary ?? 'No device'}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(ticket.repairs?.length
                      ? ticket.repairs
                      : ticket.repairSummary
                        ? [ticket.repairSummary]
                        : []
                    ).map((repair, index) => (
                      <span
                        key={`${ticket.id}-${repair}-${index}`}
                        className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600"
                      >
                        {repair}
                      </span>
                    ))}
                    {!ticket.repairs?.length && !ticket.repairSummary ? (
                      <span className="text-sm text-slate-500">No repairs</span>
                    ) : null}
                  </div>
                  <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span>👤 {ticket.customerDisplayName ?? ticket.customerNameOverride ?? 'Customer'}</span>
                      <CustomerStatusBadge
                        status={ticket.customerStatus}
                        vipLabel={vipLabel ?? null}
                        flaggedLabel={flaggedLabel ?? null}
                      />
                    </div>
                    <div>🔧 {ticket.assignedTechName ?? 'Unassigned'}</div>
                    <div className={DUE_TONE_CLASSES[countdown.tone]}>{countdown.label}</div>
                    <div>⏱ Created {formatDateTime(ticket.createdAt)}</div>
                  </div>
                  {ticket.customerComplaint?.trim() ? (
                    <p
                      className="mt-3 line-clamp-2 text-sm italic text-slate-600"
                      title={ticket.customerComplaint}
                    >
                      “{ticket.customerComplaint.trim()}”
                    </p>
                  ) : null}
                  {ticket.priority !== 'normal' || Number(ticket.depositAmount ?? 0) > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {ticket.depositRequired &&
                      depositRemaining(ticket.depositAmount, ticket.depositCollected) > 0 ? (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">
                          💰 Deposit Due
                        </span>
                      ) : Number(ticket.depositCollected ?? 0) > 0 ? (
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                          💰 {formatCurrency(ticket.depositCollected)} dep
                        </span>
                      ) : null}
                      {ticket.priority !== 'normal' ? (
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-bold ${priorityBadgeClasses(ticket.priority)}`}
                        >
                          {priorityBadgeLabel(ticket.priority)}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <TicketTable
          tickets={tickets}
          now={now}
          selectedIds={selectedIds}
          allSelected={allSelected}
          onOpen={(id) => navigate(openTicket(id))}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          vipLabel={vipLabel ?? null}
          flaggedLabel={flaggedLabel ?? null}
          canClone={canClone}
          onClone={(ticket) =>
            setCloneTarget({
              id: ticket.id,
              ticketNumber: ticket.ticketNumber,
              customerId: ticket.customerId ?? null,
            })
          }
          canDelete={canDelete}
          onDelete={(ticket) => setDeleteTarget(ticket)}
          statusCatalog={statusCatalog}
        />
      )}

      {cloneTarget ? (
        <CloneTicketModal
          ticketId={cloneTarget.id}
          ticketNumber={cloneTarget.ticketNumber}
          currentCustomerId={cloneTarget.customerId ?? null}
          open
          onClose={() => setCloneTarget(null)}
        />
      ) : null}

      <DeleteTicketModal
        open={Boolean(deleteTarget)}
        ticket={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          void queryClient.invalidateQueries({ queryKey: ['tickets'] });
          void queryClient.invalidateQueries({ queryKey: ['ticket-counts'] });
        }}
      />

      {bulkDeleteOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-950">
              Delete {selectedIds.size} ticket{selectedIds.size === 1 ? '' : 's'}?
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              This is permanent. Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              placeholder="Reason (required)"
              value={bulkDeleteReason}
              onChange={(e) => setBulkDeleteReason(e.target.value)}
            />
            <input
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              placeholder="Type DELETE"
              value={bulkDeleteConfirm}
              onChange={(e) => setBulkDeleteConfirm(e.target.value)}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl px-3 py-2 text-sm font-semibold text-slate-600"
                onClick={() => setBulkDeleteOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  bulkDeleteConfirm !== 'DELETE' ||
                  bulkDeleteReason.trim().length < 3 ||
                  bulkDeleteMutation.isPending
                }
                className="rounded-xl bg-red-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                onClick={() => void handleBulkDelete()}
              >
                {bulkDeleteMutation.isPending ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}

      {ticketsQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <TicketCardSkeleton key={i} />
          ))}
        </div>
      ) : null}
      {!ticketsQuery.isLoading && tickets.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <EmptyState
            icon="🎫"
            title="No tickets found"
            description="No tickets match your current filters. Try adjusting the filters or create a new ticket."
            action={{ label: 'Create Ticket', onClick: () => navigate('/tickets/quick') }}
          />
        </div>
      ) : null}

      {view !== 'kanban' ? (
        <Pagination
          page={filters.page}
          pageSize={filters.pageSize}
          total={ticketsMeta?.total ?? 0}
          onPageChange={(next) => updateFilters({ page: next })}
        />
      ) : null}

      {selectedIds.size > 0 ? (
        <div className="sticky bottom-4 z-20 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-lg">
          <span className="text-sm font-semibold text-slate-700">
            {selectedIds.size} ticket{selectedIds.size === 1 ? '' : 's'} selected
          </span>
          <button
            type="button"
            className="text-sm font-semibold text-primary"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear
          </button>
          <div className="mx-1 h-6 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Move to:</span>
            <select
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
            >
              <option value="">Select status...</option>
              {bulkStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {formatTicketStatus(status, statusCatalog)}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!bulkStatus || bulkMutation.isPending}
              onClick={() =>
                bulkMutation.mutate({ ticketIds: Array.from(selectedIds), status: bulkStatus })
              }
              className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Apply
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Assign to:</span>
            <select
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={bulkAssignTo}
              onChange={(e) => setBulkAssignTo(e.target.value)}
            >
              <option value="">Select tech...</option>
              {assignableUsers.map((tech) => (
                <option key={tech.id} value={tech.id}>
                  {tech.firstName} {tech.lastName}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!bulkAssignTo || bulkMutation.isPending}
              onClick={() =>
                bulkMutation.mutate({
                  ticketIds: Array.from(selectedIds),
                  assignedTo: bulkAssignTo,
                })
              }
              className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Apply
            </button>
          </div>
          <button
            type="button"
            onClick={() => toastInfo('Print labels coming soon')}
            className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Printer className="h-4 w-4" />
            Print Labels
          </button>
          {canDelete ? (
            <button
              type="button"
              onClick={() => {
                setBulkDeleteReason('');
                setBulkDeleteConfirm('');
                setBulkDeleteOpen(true);
              }}
              className="inline-flex items-center gap-2 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-800 hover:bg-red-100"
            >
              Delete Selected
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FilterTag({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
      {label}
      <button onClick={onRemove} type="button">
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

function TicketTable({
  tickets,
  now,
  selectedIds,
  allSelected,
  onOpen,
  onToggleSelect,
  onToggleSelectAll,
  vipLabel,
  flaggedLabel,
  canClone,
  onClone,
  canDelete,
  onDelete,
  statusCatalog,
}: {
  tickets: TicketListItem[];
  now: number;
  selectedIds: Set<string>;
  allSelected: boolean;
  onOpen: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  vipLabel?: string | null;
  flaggedLabel?: string | null;
  canClone?: boolean;
  onClone?: (ticket: TicketListItem) => void;
  canDelete?: boolean;
  onDelete?: (ticket: TicketListItem) => void;
  statusCatalog?: TicketStatusRecord[];
}) {
  const [tableSort, setTableSort] = useState<TableSortKey>('ticket');
  const sortedTickets = useMemo(() => {
    return [...tickets].sort((a, b) => {
      if (tableSort === 'customer') {
        return (a.customerDisplayName ?? '').localeCompare(b.customerDisplayName ?? '');
      }
      if (tableSort === 'due_date') {
        return (
          +new Date(a.promisedDate ?? '9999-12-31') - +new Date(b.promisedDate ?? '9999-12-31')
        );
      }
      if (tableSort === 'balance') {
        return Number(b.balanceDue ?? 0) - Number(a.balanceDue ?? 0);
      }
      if (tableSort === 'status') {
        return a.status.localeCompare(b.status);
      }
      return b.ticketNumber.localeCompare(a.ticketNumber, undefined, { numeric: true });
    });
  }, [tickets, tableSort]);

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleSelectAll}
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                aria-label="Select all tickets"
              />
            </th>
            <th className="px-4 py-3">
              <button onClick={() => setTableSort('ticket')}>Ticket #</button>
            </th>
            <th className="px-4 py-3">
              <button onClick={() => setTableSort('customer')}>Customer</button>
            </th>
            <th className="px-4 py-3">Device</th>
            <th className="px-4 py-3">Repairs</th>
            <th className="px-4 py-3">
              <button onClick={() => setTableSort('status')}>Status</button>
            </th>
            <th className="px-4 py-3">Priority</th>
            <th className="px-4 py-3">Assigned</th>
            <th className="px-4 py-3">
              <button onClick={() => setTableSort('due_date')}>Due Date</button>
            </th>
            <th className="px-4 py-3">
              <button onClick={() => setTableSort('balance')}>Balance</button>
            </th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sortedTickets.map((ticket) => {
            const overdue = isOverdue(ticket);
            const balance = Number(ticket.balanceDue ?? 0);
            const selected = selectedIds.has(ticket.id);
            return (
              <tr
                key={ticket.id}
                className="border-l-4"
                style={{
                  borderLeftColor: cardBorderColor(ticket, overdue),
                  backgroundColor: overdue ? '#FEF2F2' : selected ? '#F8FAFC' : undefined,
                }}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleSelect(ticket.id)}
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                    aria-label={`Select ticket ${ticket.ticketNumber}`}
                  />
                </td>
                <td className="whitespace-nowrap px-4 py-3 font-black text-primary">
                  <span className="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      className="font-black text-primary hover:underline"
                      onClick={() => onOpen(ticket.id)}
                    >
                      {ticket.ticketNumber}
                    </button>
                    <RepairDeskBadge importedFrom={ticket.importedFrom} />
                    <WarrantyBadge isWarranty={ticket.isWarranty} />
                  </span>
                </td>
                <td className="px-4 py-3" title={ticket.customerPhone ?? undefined}>
                  <span className="inline-flex items-center gap-1.5">
                    {ticket.customerDisplayName ?? 'Customer'}
                    <CustomerStatusBadge
                      status={ticket.customerStatus}
                      vipLabel={vipLabel ?? null}
                      flaggedLabel={flaggedLabel ?? null}
                    />
                  </span>
                </td>
                <td className="px-4 py-3">
                  {firstDevice(ticket)}
                  {(ticket.deviceCount ?? 0) > 1 ? ` (+${(ticket.deviceCount ?? 1) - 1} more)` : ''}
                </td>
                <td className="max-w-xs truncate px-4 py-3" title={ticket.customerComplaint?.trim() || ticket.repairSummary || undefined}>
                  {ticket.repairSummary || '-'}
                  {ticket.customerComplaint?.trim() ? (
                    <div className="mt-0.5 truncate text-xs italic text-slate-500" title={ticket.customerComplaint}>
                      {ticket.customerComplaint.trim()}
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <StatusBadge status={ticket.status} catalog={statusCatalog} />
                    {ticket.depositRequired &&
                    depositRemaining(ticket.depositAmount, ticket.depositCollected) > 0 ? (
                      <span className="text-xs font-semibold text-amber-700">💰 Deposit due</span>
                    ) : Number(ticket.depositCollected ?? 0) > 0 ? (
                      <span className="text-xs text-slate-500">
                        💰 {formatCurrency(ticket.depositCollected)} dep
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {ticket.priority !== 'normal' ? (
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-bold ${priorityBadgeClasses(ticket.priority)}`}
                    >
                      {priorityBadgeLabel(ticket.priority)}
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
                <td className="px-4 py-3">
                  {ticket.assignedTechName ? (
                    <span
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-white"
                      title={ticket.assignedTechName}
                    >
                      {initials(ticket.assignedTechName)}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className={`px-4 py-3 ${overdue ? 'font-bold text-red-700' : 'text-slate-600'}`}>
                  <div className="flex items-center gap-2">
                    <span>{dueLabel(ticket.promisedDate)}</span>
                    {overdue ? (
                      <span className="whitespace-nowrap rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                        OVERDUE
                      </span>
                    ) : null}
                  </div>
                </td>
                <td
                  className={`px-4 py-3 font-semibold ${balance > 0 ? 'text-red-600' : 'text-green-700'}`}
                >
                  {balance > 0 ? formatCurrency(balance) : 'Paid'}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    {canClone && onClone ? (
                      <button
                        type="button"
                        className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold"
                        onClick={() => onClone(ticket)}
                      >
                        Clone
                      </button>
                    ) : null}
                    {canDelete && onDelete ? (
                      <button
                        type="button"
                        className="rounded-lg border border-red-300 px-3 py-1.5 font-semibold text-red-800 hover:bg-red-50"
                        onClick={() => onDelete(ticket)}
                      >
                        Delete
                      </button>
                    ) : null}
                    <button
                      className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold"
                      onClick={() => onOpen(ticket.id)}
                    >
                      Open
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
